import React, { createContext, useCallback, useState } from 'react';
import { lookupAccount, getCurrentBill, getBillingHistory, submitPayment, isMockBilling } from '../services/billingApi';

const BillingContext = createContext(null);

export const BillingProvider = ({ children }) => {
  const [account, setAccount] = useState(null); // account metadata
  const [currentBill, setCurrentBill] = useState(null);
  const [billLoaded, setBillLoaded] = useState(false); // true once bill fetch completes (even if no bill exists)
  const [billingHistory, setBillingHistory] = useState([]);
  const [paymentResult, setPaymentResult] = useState(null);
  const [loading, setLoading] = useState({ lookup: false, bill: false, history: false, payment: false });
  const [error, setError] = useState(null);

  const clearErrors = () => setError(null);

  const performLookup = useCallback(async ({ accountNumber, phoneNumber }) => {
    setLoading(l => ({ ...l, lookup: true }));
    setError(null);
    setAccount(null);
    setCurrentBill(null);
    setBillLoaded(false);
    setBillingHistory([]);
    try {
      const result = await lookupAccount({ accountNumber, phoneNumber });
      if (!result.success) {
        setError(result.error || 'Lookup failed');
        return false;
      }
      setAccount(result.account);
      return true;
    } catch (e) {
      setError(e.message || 'Lookup error');
      return false;
    } finally {
      setLoading(l => ({ ...l, lookup: false }));
    }
  }, []);

  const loadCurrentBill = useCallback(async () => {
    if (!account?.accountNumber) return;
    setLoading(l => ({ ...l, bill: true }));
    setError(null);
    try {
      const result = await getCurrentBill(account.accountNumber);
      if (!result.success) {
        // "Bill not found" is a normal state (e.g. after payment), not an error
        setCurrentBill(null);
        setBillLoaded(true);
        return true;
      }
      setCurrentBill(result.bill);
      setBillLoaded(true);
      return true;
    } catch (e) {
      setError(e.message || 'Bill error');
      return false;
    } finally {
      setLoading(l => ({ ...l, bill: false }));
    }
  }, [account]);

  const loadBillingHistory = useCallback(async () => {
    if (!account?.accountNumber) return;
    setLoading(l => ({ ...l, history: true }));
    setError(null);
    try {
      const result = await getBillingHistory(account.accountNumber);
      if (!result.success) {
        setError(result.error || 'Billing history retrieval failed');
        return false;
      }
      setBillingHistory(result.bills || []);
      return true;
    } catch (e) {
      setError(e.message || 'Billing history error');
      return false;
    } finally {
      setLoading(l => ({ ...l, history: false }));
    }
  }, [account]);

  const processPayment = useCallback(async ({ amount, method, cardNumber }) => {
    if (!account?.accountNumber || !currentBill) return { success: false, error: 'No account/bill' };
    setLoading(l => ({ ...l, payment: true }));
    setError(null);
    setPaymentResult(null);
    try {
      const result = await submitPayment({
        accountNumber: account.accountNumber,
        amount: Number(amount),
        method,
        cardLast4: cardNumber ? cardNumber.replace(/\D/g, '').slice(-4) : undefined
      });
      if (!result.success) {
        setError(result.error || 'Payment failed');
        return result;
      }
      setPaymentResult(result.payment);
      // Optimistically update bill status if full payment
      setCurrentBill(b => b ? { ...b, status: 'PAID', totals: { ...b.totals, amountDue: 0 } } : b);
      return result;
    } catch (e) {
      const errMsg = e.message || 'Payment error';
      setError(errMsg);
      return { success: false, error: errMsg };
    } finally {
      setLoading(l => ({ ...l, payment: false }));
    }
  }, [account, currentBill]);

  const resetPaymentResult = () => setPaymentResult(null);

  const resetBillingSession = useCallback(() => {
    setAccount(null);
    setCurrentBill(null);
    setBillLoaded(false);
    setBillingHistory([]);
    setPaymentResult(null);
    setError(null);
  }, []);

  const value = {
    account,
    currentBill,
    billLoaded,
    billingHistory,
    paymentResult,
    loading,
    error,
    clearErrors,
    performLookup,
    loadCurrentBill,
    loadBillingHistory,
    processPayment,
    resetPaymentResult,
    resetBillingSession,
    isMock: isMockBilling()
  };

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
};

export default BillingContext;
